import { todayKey, dateKey } from "./helpers";

// Seeded pseudo-random — deterministic for a given seed
function seededRandom(seed) {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

// ── Quest history (localStorage) ────────────────────────────────────────────
// Tracks which quest IDs were shown each day to prevent stale repeats.

const HISTORY_KEY = "sentinel_quest_history";

function loadQuestHistory() {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveQuestHistory(history) {
  try {
    // Trim to last 7 days
    const keys = Object.keys(history).sort().slice(-7);
    const trimmed = {};
    keys.forEach((k) => { trimmed[k] = history[k]; });
    localStorage.setItem(HISTORY_KEY, JSON.stringify(trimmed));
  } catch { /* storage unavailable */ }
}

function getRecentlyShownIds(daysBack = 2) {
  const history = loadQuestHistory();
  const today = new Date();
  const recent = new Set();
  for (let i = 1; i <= daysBack; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    (history[k] || []).forEach((id) => recent.add(id));
  }
  return recent;
}

function recordShownQuests(quests) {
  const history = loadQuestHistory();
  history[todayKey()] = quests.map((q) => q.id);
  saveQuestHistory(history);
}

// Pick one quest from a pool, preferring quests not shown recently.
// Falls back to full pool if all have been shown recently.
function pickOne(pool, seed, recentIds = new Set()) {
  const fresh = pool.filter((q) => !recentIds.has(q.id));
  const candidates = fresh.length > 0 ? fresh : pool;
  return candidates[Math.floor(seededRandom(seed) * candidates.length)];
}

// ── Quest definitions grouped by category ──────────────────────────────────

const MOVEMENT_QUESTS = [
  {
    id: "log_workout",
    title: "Iron Session",
    description: "Complete a workout today",
    emoji: "🏋️",
    xp: 50,
    goal: 1,
    unit: "workout",
    check: ({ workouts }) => {
      const today = todayKey();
      return workouts.some((w) => dateKey(w.startTime) === today);
    },
    progressVal: ({ workouts }) => {
      const today = todayKey();
      return workouts.some((w) => dateKey(w.startTime) === today) ? 1 : 0;
    },
  },
  {
    id: "log_run",
    title: "Hit the Pavement",
    description: "Log a run today",
    emoji: "🏃",
    xp: 40,
    goal: 1,
    unit: "run",
    check: ({ runs }) => {
      const today = todayKey();
      return runs.some((r) => dateKey(r.timestamp) === today);
    },
    progressVal: ({ runs }) => {
      const today = todayKey();
      return runs.some((r) => dateKey(r.timestamp) === today) ? 1 : 0;
    },
  },
  {
    id: "train_and_eat",
    title: "Complete Day",
    description: "Log a workout AND 2+ meals today",
    emoji: "⚡",
    xp: 65,
    goal: 3,
    unit: "tasks",
    check: ({ workouts, runs, mealLog }) => {
      const today = todayKey();
      const hasTrained =
        workouts.some((w) => dateKey(w.startTime) === today) ||
        runs.some((r) => dateKey(r.timestamp) === today);
      const mealCount = (mealLog[today] || []).filter((m) => m.mealId !== "__water__").length;
      return hasTrained && mealCount >= 2;
    },
    progressVal: ({ workouts, runs, mealLog }) => {
      const today = todayKey();
      const hasTrained =
        workouts.some((w) => dateKey(w.startTime) === today) ||
        runs.some((r) => dateKey(r.timestamp) === today)
          ? 1
          : 0;
      const mealCount = Math.min(
        (mealLog[today] || []).filter((m) => m.mealId !== "__water__").length,
        2
      );
      return hasTrained + mealCount;
    },
  },
];

const NUTRITION_QUESTS = [
  {
    id: "hit_protein",
    title: "Protein Target",
    description: "Hit 90% of your daily protein goal",
    emoji: "🥩",
    xp: 35,
    goal: 1,
    unit: "goal",
    isPercent: true,
    check: ({ todayTotals, macroGoals }) => {
      if (!macroGoals?.protein || !todayTotals?.protein) return false;
      return todayTotals.protein >= macroGoals.protein * 0.9;
    },
    progressVal: ({ todayTotals, macroGoals }) => {
      if (!macroGoals?.protein) return 0;
      return Math.min(1, (todayTotals?.protein || 0) / (macroGoals.protein * 0.9));
    },
  },
  {
    id: "log_3_meals",
    title: "Full Day Fueling",
    description: "Log all 3 meals today",
    emoji: "🍽️",
    xp: 30,
    goal: 3,
    unit: "meals",
    check: ({ mealLog }) => {
      const today = todayKey();
      return (mealLog[today] || []).filter((m) => m.mealId !== "__water__").length >= 3;
    },
    progressVal: ({ mealLog }) => {
      const today = todayKey();
      return Math.min(
        (mealLog[today] || []).filter((m) => m.mealId !== "__water__").length,
        3
      );
    },
  },
  {
    id: "calorie_goal",
    title: "Calorie On-Track",
    description: "Stay within 150 calories of your daily goal",
    emoji: "🎯",
    xp: 25,
    goal: 1,
    unit: "goal",
    isPercent: true,
    check: ({ todayTotals, macroGoals }) => {
      if (!macroGoals?.calories || !todayTotals?.calories) return false;
      return (
        todayTotals.calories > 0 &&
        Math.abs(todayTotals.calories - macroGoals.calories) <= 150
      );
    },
    progressVal: ({ todayTotals, macroGoals }) => {
      if (!macroGoals?.calories || !todayTotals?.calories) return 0;
      return Math.abs(todayTotals.calories - macroGoals.calories) <= 150 ? 1 : 0;
    },
  },
];

const TRACKING_QUESTS = [
  {
    id: "log_weight",
    title: "Daily Check-In",
    description: "Log your weight today",
    emoji: "⚖️",
    xp: 20,
    goal: 1,
    unit: "weigh-in",
    check: ({ weightLog }) => {
      const today = todayKey();
      return (weightLog || []).some((w) => {
        const d = w.date ? String(w.date).split("T")[0] : null;
        return d === today;
      });
    },
    progressVal: ({ weightLog }) => {
      const today = todayKey();
      return (weightLog || []).some((w) => {
        const d = w.date ? String(w.date).split("T")[0] : null;
        return d === today;
      })
        ? 1
        : 0;
    },
  },
  {
    id: "active_streak",
    title: "Streak Builder",
    description: "Train or log 3 meals to keep your streak alive",
    emoji: "🔥",
    xp: 20,
    goal: 1,
    unit: "activity",
    check: ({ workouts, runs, mealLog }) => {
      const today = todayKey();
      const trained =
        workouts.some((w) => dateKey(w.startTime) === today) ||
        runs.some((r) => dateKey(r.timestamp) === today);
      const mealCount = (mealLog[today] || []).filter(
        (m) => m.mealId !== "__water__"
      ).length;
      return trained || mealCount >= 3;
    },
    progressVal: ({ workouts, runs, mealLog }) => {
      const today = todayKey();
      const trained =
        workouts.some((w) => dateKey(w.startTime) === today) ||
        runs.some((r) => dateKey(r.timestamp) === today);
      if (trained) return 1;
      const mealCount = (mealLog[today] || []).filter(
        (m) => m.mealId !== "__water__"
      ).length;
      return mealCount >= 3 ? 1 : 0;
    },
  },
];

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Returns today's 3 daily quests (one from each category),
 * deterministically seeded by the current calendar day.
 */
export function getDailyQuests(data = {}) {
  const dayNum = Math.floor(Date.now() / 86400000);
  const safe = {
    workouts: data.workouts || [],
    runs: data.runs || [],
    mealLog: data.mealLog || {},
    todayTotals: data.todayTotals || {},
    macroGoals: data.macroGoals || {},
    weightLog: data.weightLog || [],
  };

  // Behavior-aware selection: avoid quests shown in the last 2 days
  const recentIds = getRecentlyShownIds(2);

  const quests = [
    pickOne(MOVEMENT_QUESTS, dayNum, recentIds),
    pickOne(NUTRITION_QUESTS, dayNum + 111, recentIds),
    pickOne(TRACKING_QUESTS, dayNum + 222, recentIds),
  ];

  // Persist today's selection so tomorrow's rotation knows what to avoid
  recordShownQuests(quests);

  return quests.map((quest) => {
    const completed = quest.check(safe);
    const progress = quest.progressVal(safe);
    const progressPercent = Math.min(100, (progress / quest.goal) * 100);
    return { ...quest, completed, progress, progressPercent };
  });
}

export function getDailyQuestXp(data = {}) {
  return getDailyQuests(data)
    .filter((q) => q.completed)
    .reduce((sum, q) => sum + q.xp, 0);
}

export function getDailyQuestSummary(data = {}) {
  const quests = getDailyQuests(data);
  const completed = quests.filter((q) => q.completed).length;
  const xp = quests.filter((q) => q.completed).reduce((s, q) => s + q.xp, 0);
  return { quests, completed, total: quests.length, xp };
}
